import { ContextMenuHandler } from "@asup/context-menu";
import { ReactNode } from "react";
import { AioIconButton } from "../../additional/aioIconButton";
import styles from "../../aic.module.css";
import { EditorDefns } from "../../utility";
import { useArsContext } from "../contexts";
import { ArsPageByItem } from "./interface";

export interface ArsBlockLineProps<T> {
  disabled?: boolean;
  editorDefns: EditorDefns<T>;
  pageByIndex: number;
  value: ArsPageByItem<T>[];
  setValue: (newValue: ArsPageByItem<T>[]) => void;
}

/**
 * Renders a single ARS page-by row using TLF and ARS context data.
 *
 * @param props - Component props
 * @param props.disabled - When true the line is read only
 * @param props.editorDefns - Editor defs used to render label overrides
 * @param props.pageByIndex - Index of the page-by item in TLF context
 * @param props.setValue - Callback fired when the page-by item label changes
 * @param props.value - Ordered page-by items currently selected
 * @returns Rendered line or null when the current TLF item is not an ARS page-by row
 */
export const ArsBlockLine = <T,>({
  disabled = false,
  editorDefns,
  pageByIndex,
  setValue,
  value,
}: ArsBlockLineProps<T>): ReactNode => {
  const { state: arsState } = useArsContext();

  const pageByItem = value[pageByIndex];
  const groupingFactor = arsState.groupingFactors[pageByItem.groupingFactorId];
  const label: T =
    pageByItem.label !== undefined
      ? pageByItem.label
      : editorDefns.textToT(
          groupingFactor?.label ??
            groupingFactor?.name ??
            // istanbul ignore next
            "Unknown grouping factor",
        );

  /**
   * Remove the current line from the array
   */
  const removeLine = () => {
    setValue(value.filter((_, itemIndex) => itemIndex !== pageByIndex));
  };

  return (
    <div className={styles.aibLine}>
      <div className={styles.aibLineButtons} />
      <ContextMenuHandler
        className={styles.calcSize}
        menuItems={
          disabled
            ? []
            : [
                {
                  label: "Delete line",
                  action: removeLine,
                },
              ]
        }
      >
        <div className={styles.aibLineItemHolder}>
          <div className={styles.flexGrow1}>
            <div className={styles.aibLineItem}>
              <editorDefns.Editor
                id={`ars-block-line-editor-${pageByIndex}`}
                value={label}
                editable={!disabled}
                setValue={(ret: T) => {
                  const newPageItem: ArsPageByItem<T> = {
                    ...pageByItem,
                    label: ret,
                  };
                  const newPageBy = value.map((item, index) =>
                    index === pageByIndex ? newPageItem : item,
                  );
                  setValue(newPageBy);
                }}
              />
            </div>
          </div>
        </div>
      </ContextMenuHandler>
      <div className={styles.aibLineButtons}>
        {!disabled ? (
          <AioIconButton
            id={`ars-block-remove-line-${pageByIndex}`}
            iconName="aioxMinus"
            onClick={removeLine}
            tipText="Remove page by line"
          />
        ) : null}
      </div>
    </div>
  );
};

ArsBlockLine.displayName = "ArsBlockLine";
